import { cva } from "class-variance-authority";

// The look of a choice tile, shared by VRadioTile (one choice) and VCheckboxTile (several choices); only the
// indicator differs. `invalid` is only set by a checkbox tile group whose selection has an error: its red border is
// important so it wins over the selected, hover and dark borders.

// Board: 1px #EEEEEE border, 14px radius, 10px 12px padding; selected 2px primary border on #F3F9F3 with one pixel
// less padding so the content does not move; disabled is dashed on #FAFAFA. The `lg` tile (a stacked choice such as
// the rate limit of a new app) has a 16px radius, 14px 16px padding and a 14px gap.
export const choiceTileCva = cva(
  "flex items-center text-left transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary-500/40",
  {
    variants: {
      selected: {
        true: "border-2 border-primary-600 bg-primary-50/60 dark:bg-primary-500/10",
        false: "border border-gray-200 bg-white dark:border-dark-600 dark:bg-dark-800",
      },
      size: {
        md: "gap-3 rounded-[14px]",
        lg: "gap-3.5 rounded-2xl",
      },
      disabled: {
        true: "cursor-not-allowed",
        false: "cursor-pointer",
      },
      invalid: {
        true: "border-danger-600!",
        false: null,
      },
    },
    compoundVariants: [
      { selected: true, size: "md", class: "px-[11px] py-[9px]" },
      { selected: false, size: "md", class: "px-3 py-2.5" },
      { selected: true, size: "lg", class: "px-[15px] py-[13px]" },
      { selected: false, size: "lg", class: "px-4 py-3.5" },
      {
        selected: false,
        disabled: true,
        class: "border-dashed border-gray-300 bg-gray-50 dark:border-dark-600 dark:bg-dark-850",
      },
      {
        selected: false,
        disabled: false,
        class: "hover:border-gray-300 dark:hover:border-dark-500",
      },
    ],
    defaultVariants: {
      selected: false,
      size: "md",
      disabled: false,
      invalid: false,
    },
  },
);

// The size sets the text size of both variants; the stacked board uses a softer title (#424242) and subtitle
// (#757575, 16px lines) than the row tile.
export const choiceTileTitleCva = cva("font-medium", {
  variants: {
    disabled: {
      true: "text-gray-500 dark:text-gray-500",
      false: null,
    },
    size: {
      md: "text-sm",
      lg: "text-[15px]",
    },
    variant: {
      row: null,
      stacked: "leading-5",
    },
  },
  compoundVariants: [
    { disabled: false, variant: "row", class: "text-(--text)" },
    { disabled: false, variant: "stacked", class: "text-gray-800 dark:text-gray-100" },
  ],
  defaultVariants: {
    disabled: false,
    size: "md",
    variant: "row",
  },
});

export const choiceTileSubtitleCva = cva("", {
  variants: {
    size: {
      md: "text-xs",
      lg: "text-[13px]",
    },
    variant: {
      row: "text-gray-700 dark:text-gray-300",
      stacked: "text-gray-600 dark:text-gray-400",
    },
  },
  compoundVariants: [
    { variant: "row", size: "md", class: "leading-[1.4]" },
    { variant: "row", size: "lg", class: "leading-[1.45]" },
    { variant: "stacked", size: "md", class: "leading-4" },
    { variant: "stacked", size: "lg", class: "leading-[18px]" },
  ],
  defaultVariants: {
    size: "md",
    variant: "row",
  },
});

export const choiceTileBodyCva = cva("flex min-w-0 flex-1 flex-col", {
  variants: {
    size: {
      md: "gap-0.5",
      lg: "gap-[3px]",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

// Stacked tile (board: bulk assign operations): 8px radius, 10px 10px 12px padding and an 8px gap between the scene
// and the text row; selected is a 2px primary border with one pixel less padding, so the content does not move.
export const choiceTileStackedCva = cva(
  "flex flex-col items-stretch gap-2 rounded-lg text-left transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary-500/40",
  {
    variants: {
      selected: {
        true: "border-2 border-primary-600 bg-primary-50/60 px-[9px] pt-[9px] pb-[11px] dark:bg-primary-500/10",
        false: "border border-gray-300 bg-white px-2.5 pt-2.5 pb-3 dark:border-dark-600 dark:bg-dark-800",
      },
      disabled: {
        true: "cursor-not-allowed",
        false: "cursor-pointer",
      },
      invalid: {
        true: "border-danger-600!",
        false: null,
      },
    },
    compoundVariants: [
      {
        selected: false,
        disabled: true,
        class: "border-dashed border-gray-300 bg-gray-50 dark:border-dark-600 dark:bg-dark-850",
      },
      {
        selected: false,
        disabled: false,
        class: "hover:border-gray-400 dark:hover:border-dark-500",
      },
    ],
    defaultVariants: {
      selected: false,
      disabled: false,
      invalid: false,
    },
  },
);

/** The 64px scene box over the full width: light gray, white on the light primary background of the chosen tile. */
export const choiceTileSceneCva = cva("flex h-16 items-center justify-center overflow-hidden rounded-md", {
  variants: {
    selected: {
      true: "bg-white dark:bg-dark-800",
      false: "bg-gray-100 dark:bg-dark-700",
    },
  },
  defaultVariants: {
    selected: false,
  },
});
